/**
 * Centralized Error Tracking Service
 * 
 * Uses Sentry for production error monitoring with:
 * - Automatic scrubbing of sensitive data (patient info)
 * - User context tracking (doctor ID only, no PII)
 * - Custom breadcrumbs for sync operations
 * - Focused on failures to minimize quota usage
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// Patterns to scrub from error data
const SENSITIVE_PATTERNS = [
  /phone[_\s]?(?:number)?/i,
  /patient[_\s]?name/i,
  /email/i,
  /address/i,
];

/**
 * Initialize Sentry - call once on app startup
 */
export function initErrorTracking() {
  if (!SENTRY_DSN) {
    console.log('[ErrorTracking] Sentry DSN not configured, errors will only log to console');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: IS_PRODUCTION ? 'production' : 'development',

    // Only send errors and critical breadcrumbs to conserve quota
    tracesSampleRate: 0, // Disable performance tracing to save quota
    replaysSessionSampleRate: 0, // Disable session replays
    replaysOnErrorSampleRate: 0, // Disable replays on error

    // Only capture errors, not warnings or info
    beforeSend(event) {
      // Scrub sensitive data from error messages
      if (event.message) {
        event.message = scrubSensitiveData(event.message);
      }

      // Scrub from exception values
      if (event.exception?.values) {
        event.exception.values.forEach(ex => {
          if (ex.value) {
            ex.value = scrubSensitiveData(ex.value);
          }
        });
      }

      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // Only keep essential breadcrumbs (navigation, errors, custom)
      const allowedTypes = ['navigation', 'error', 'info'];
      if (!allowedTypes.includes(breadcrumb.category)) {
        // Allow custom breadcrumbs we explicitly add
        if (!breadcrumb.category?.startsWith('sync.') &&
          !breadcrumb.category?.startsWith('auth.')) {
          return null;
        }
      }
      return breadcrumb;
    },

    integrations: [
      // Remove default integrations that generate too many events
      Sentry.replayIntegration && Sentry.replayIntegration({ maskAllText: true }),
    ].filter(Boolean),
  });

  console.log('[ErrorTracking] Sentry initialized');
}

/**
 * Scrub potentially sensitive data from strings
 */
function scrubSensitiveData(text) {
  if (!text || typeof text !== 'string') return text;

  let scrubbed = text;

  // Scrub phone numbers (10+ digit patterns)
  scrubbed = scrubbed.replace(/\b\d{10,}\b/g, '[PHONE_REDACTED]');

  // Scrub email patterns
  scrubbed = scrubbed.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]');

  return scrubbed;
}

/**
 * Set the current user context (doctor ID for correlation, no PII)
 */
export function setUserContext(userId) {
  if (!SENTRY_DSN) return;

  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add a breadcrumb for sync operations
 */
export function addSyncBreadcrumb(operation, data = {}) {
  const breadcrumb = {
    category: `sync.${operation}`,
    message: `Sync: ${operation}`,
    level: 'info',
    data: {
      ...data,
      timestamp: new Date().toISOString(),
    },
  };

  if (SENTRY_DSN) {
    Sentry.addBreadcrumb(breadcrumb);
  } else {
    console.log('[Breadcrumb]', breadcrumb);
  }
}

/**
 * Add a breadcrumb for auth operations
 */
export function addAuthBreadcrumb(operation, data = {}) {
  const breadcrumb = {
    category: `auth.${operation}`,
    message: `Auth: ${operation}`,
    level: 'info',
    data: {
      ...data,
      timestamp: new Date().toISOString(),
    },
  };

  if (SENTRY_DSN) {
    Sentry.addBreadcrumb(breadcrumb);
  } else {
    console.log('[Breadcrumb]', breadcrumb);
  }
}

/**
 * Capture an error with additional context
 * Use this instead of console.error for important errors
 */
export function captureError(error, context = {}) {
  // Always log to console for development
  console.error('[CapturedError]', error, context);

  if (!SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    // Add context as extra data
    Object.entries(context).forEach(([key, value]) => {
      // Scrub sensitive fields
      if (SENSITIVE_PATTERNS.some(pattern => pattern.test(key))) {
        scope.setExtra(key, '[REDACTED]');
      } else {
        scope.setExtra(key, value);
      }
    });

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), 'error');
    }
  });
}

/**
 * Track a critical event (auth failures, data clearing, etc.)
 * These are important events we always want to capture
 */
export function trackCriticalEvent(eventName, data = {}) {
  const eventData = {
    event: eventName,
    ...data,
    timestamp: new Date().toISOString(),
  };

  console.log('[CriticalEvent]', eventData);

  if (!SENTRY_DSN) return;

  Sentry.captureMessage(`Critical: ${eventName}`, {
    level: 'warning',
    extra: eventData,
  });
}

/**
 * Track when local data is cleared (important for debugging blank screen issues)
 */
export function trackDataCleared(reason) {
  trackCriticalEvent('data_cleared', { reason });
}

/**
 * Track auth failures that might lead to unexpected logouts
 */
export function trackAuthFailure(reason, details = {}) {
  trackCriticalEvent('auth_failure', { reason, ...details });
}

// Export Sentry's ErrorBoundary for use in App.js
export { Sentry };
